import React, {ReactNode, useContext} from 'react';
import {Route, Redirect, useLocation} from 'react-router-dom';
import {AuthContext} from '../../contexts/auth/context';
import {AuthContextType} from '../../contexts/auth/types';
import {InviteIdentificationType} from '../../enums/invite-identification-type';
import {ScreenPaths} from '../../enums/screen-path';
import {PersistInvite, PERSIST_INVITE} from '../../events/event-types';
import eventEmitter from '../../events/event-emitter';
import {InviteType} from '../../backend/enums/invite-type';

interface Props {
  path: string;
  children: React.ReactNode;
}

/**
 * HOC used for invite routes.
 *
 * The user will navigate to the appropriate invite screen (if logged off) or a modal will open (if signed in).
 */
const InviteRoute: React.FC<Props> = ({children, ...rest}) => {
  // Get context
  const authContext: AuthContextType = useContext(AuthContext);

  // Get location
  let location = useLocation();

  // Component to render
  let component: ReactNode | React.ReactElement = <></>;
  if (location && location.search && location.search.split('?voucherCode=')[1]) {
    // User not logged in. Show the screen.
    if (!authContext.state.firebaseUser) {
      component = children;
    } else {
      // Redirect to the office
      component = (
        <Redirect
          to={{
            pathname: ScreenPaths.Office,
          }}
        />
      );

      if (location.pathname === ScreenPaths.AcceptRelationshipInvite) {
        // Emit relationship invite to show it later on
        eventEmitter.emit(PERSIST_INVITE, {
          identificationValue: location.search.split('?voucherCode=')[1],
          identificationType: InviteIdentificationType.VoucherCode,
          inviteType: InviteType.Relationship,
        } as PersistInvite);

        // Log
        console.debug('🟢 Relationship invite persisted with values.', {
          identificationValue: location.search.split('?voucherCode=')[1],
          identificationType: InviteIdentificationType.VoucherCode,
        });
      } else if (location.pathname === ScreenPaths.AcceptAffiliationInvite) {
        // Emit affiliation invite to show it later on
        eventEmitter.emit(PERSIST_INVITE, {
          identificationValue: location.search.split('?voucherCode=')[1],
          identificationType: InviteIdentificationType.VoucherCode,
          inviteType: InviteType.Affiliation,
        } as PersistInvite);

        // Log
        console.debug('🟢 Affiliation invite persisted with values.', {
          identificationValue: location.search.split('?voucherCode=')[1],
          identificationType: InviteIdentificationType.VoucherCode,
        });
      } else {
        // Log
        console.debug('🔴 Invite not persisted.', location);
      }
    }
  } else {
    // Redirect. There's nothing to see. The path was wrong.
    component = (
      <Redirect
        to={{
          pathname: ScreenPaths.Office,
          state: {from: location},
        }}
      />
    );
  }

  return <Route {...rest} render={() => component} />;
};

export default InviteRoute;
